import React, { useEffect, useRef, useState } from "react";
import { C, font } from "../lib/styles.js";
import { LogoMark } from "./Logo.jsx";
import * as flow from "../lib/authFlow.js";
import {
  MAX_CODE_TRIES, cleanCode, cleanEmail, codeErrorText, isFullCode, looksLikeEmail,
  resetErrorText, signInErrorText,
} from "../lib/passwordSignIn.js";

/*
  The sign-in screen (docs/ops/password-signin.md). Email only: the Google
  button is gone (2026-09-29, "Google sign-in removed"), and nothing here
  starts a Google sign-in. Four views:
    choose  Sign in with email, and what to do for someone who used Google;
    email   email + password, and Forgot password;
    code    the 6-digit code from the authenticator app, after the password;
    reset   send Firebase's reset-password email (also how an account that
            only ever used Google gets its first password).
  Being signed in is App's business: this screen only starts sign-ins. The
  set-up screen for a first authenticator is AuthenticatorSetup.jsx.
  Phone sizes (mobile design pass): every tap target at least 44 px, every
  text field 16 px so iPhone Safari does not zoom in.
*/
export default function LoginScreen({ error, onDismissError, initialView = "choose" }) {
  const [view, setView] = useState(initialView);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(null); // Firebase's code step: { resolver, hint }
  const [tries, setTries] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { text, tone: "error" | "info" }
  const [sentTo, setSentTo] = useState("");

  const say = (text, tone = "error") => setMessage(text ? { text, tone } : null);
  const fresh = () => {
    if (error && onDismissError) onDismissError();
    setMessage(null);
  };
  const go = (next) => {
    fresh();
    setView(next);
  };

  const afterFirstStep = (result) => {
    if (result.status === "code-needed") {
      setStep(result);
      setCode("");
      setTries(0);
      setView("code");
      return;
    }
    if (result.status === "unsupported-factor") {
      say("This account asks for a kind of second step the Suite doesn't support. Ask Ivin.");
    }
    // signed-in: App takes over from here.
  };

  const onPassword = async (e) => {
    e.preventDefault();
    fresh();
    const addr = cleanEmail(email);
    if (!looksLikeEmail(addr)) return say("Enter your full email address.");
    if (!password) return say("Enter your password.");
    setBusy(true);
    try {
      afterFirstStep(await flow.signInWithPassword(addr, password));
    } catch (err) {
      say(signInErrorText(err?.code));
    } finally {
      setBusy(false);
    }
  };

  const onCode = async (e) => {
    e.preventDefault();
    fresh();
    if (!isFullCode(code)) return say("Enter the 6-digit code.");
    setBusy(true);
    try {
      await flow.finishCodeSignIn(step, code);
      setPassword("");
    } catch (err) {
      const r = codeErrorText(err?.code);
      const used = r.wrongCode ? tries + 1 : tries;
      setTries(used);
      setCode("");
      if (r.back || used >= MAX_CODE_TRIES) {
        setStep(null);
        setPassword("");
        setView("email");
        say(used >= MAX_CODE_TRIES && !r.back ? "Too many wrong codes. Sign in again with your password." : r.text);
      } else {
        say(r.text);
      }
    } finally {
      setBusy(false);
    }
  };

  const onReset = async (e) => {
    e.preventDefault();
    fresh();
    const addr = cleanEmail(email);
    if (!looksLikeEmail(addr)) return say("Enter your full email address.");
    setBusy(true);
    try {
      await flow.sendPasswordReset(addr);
      setSentTo(addr);
      setView("reset-sent");
    } catch (err) {
      say(resetErrorText(err?.code));
    } finally {
      setBusy(false);
    }
  };

  const shown = message ?? (error ? { text: error, tone: "error" } : null);

  return (
    <div className="signin-screen" style={styles.screen}>
      <div style={styles.panel}>
        <div style={styles.brand}>
          <LogoMark size={36} />
          <span style={styles.brandName}>NYC Suite</span>
        </div>
        <p style={styles.sub}>New York Capture</p>

        {view === "choose" && (
          <ChooseView busy={busy} onEmail={() => go("email")} />
        )}
        {view === "email" && (
          <EmailView
            email={email} password={password} busy={busy}
            onEmail={setEmail} onPassword={setPassword} onSubmit={onPassword}
            onForgot={() => go("reset")} onBack={() => go("choose")}
          />
        )}
        {view === "code" && (
          <CodeView
            code={code} busy={busy} triesLeft={MAX_CODE_TRIES - tries}
            onCode={(v) => setCode(cleanCode(v))} onSubmit={onCode}
            onBack={() => {
              setStep(null);
              setPassword("");
              go("email");
            }}
          />
        )}
        {view === "reset" && (
          <ResetView email={email} busy={busy} onEmail={setEmail} onSubmit={onReset} onBack={() => go("email")} />
        )}
        {view === "reset-sent" && (
          <ResetSentView email={sentTo} onBack={() => {
            setPassword("");
            go("email");
          }} />
        )}

        {shown && (
          <div role={shown.tone === "error" ? "alert" : "status"} style={shown.tone === "error" ? styles.error : styles.info}>
            {authDomainText(shown.text)}
          </div>
        )}

        <LocalReview />
      </div>
    </div>
  );
}

function authDomainText(text) {
  return String(text || "").includes("auth/unauthorized-domain")
    ? "Firebase auth does not allow this local domain. Use localhost for auth, or open the local review fixture below."
    : text;
}

// The first view. Someone whose account only ever signed in with Google has
// no password yet: the reset email sets the first one.
export const EMAIL_ONLY_HINT = "Signed in with Google before? Use Sign in with email. First time? Tap Forgot password? to set your password.";

export function ChooseView({ busy, onEmail }) {
  return (
    <div style={styles.stack}>
      <button type="button" style={styles.primary} onClick={onEmail} disabled={busy}>Sign in with email</button>
      <p style={styles.hint}>{EMAIL_ONLY_HINT}</p>
    </div>
  );
}

export function EmailView({ email, password, busy, onEmail, onPassword, onSubmit, onForgot, onBack }) {
  const [show, setShow] = useState(false);
  return (
    <form style={styles.stack} onSubmit={onSubmit} noValidate>
      <label style={styles.label} htmlFor="signin-email">Email</label>
      <input
        id="signin-email" name="email" type="email" inputMode="email" autoComplete="username"
        autoCapitalize="none" autoCorrect="off" spellCheck={false}
        value={email} onChange={(e) => onEmail(e.target.value)} style={styles.input} disabled={busy}
      />
      <label style={styles.label} htmlFor="signin-password">Password</label>
      <div style={styles.passwordRow}>
        <input
          id="signin-password" name="password" type={show ? "text" : "password"} autoComplete="current-password"
          autoCapitalize="none" autoCorrect="off" spellCheck={false}
          value={password} onChange={(e) => onPassword(e.target.value)} style={{ ...styles.input, flex: 1, minWidth: 0 }} disabled={busy}
        />
        <button type="button" style={styles.showButton} onClick={() => setShow((v) => !v)} aria-pressed={show} aria-label={show ? "Hide password" : "Show password"}>
          {show ? "Hide" : "Show"}
        </button>
      </div>
      <button type="submit" style={styles.primary} disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      <div style={styles.linkRow}>
        <button type="button" style={styles.link} onClick={onForgot} disabled={busy}>Forgot password?</button>
        <button type="button" style={styles.link} onClick={onBack} disabled={busy}>Back</button>
      </div>
    </form>
  );
}

export function CodeView({ code, busy, triesLeft, onCode, onSubmit, onBack }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <form style={styles.stack} onSubmit={onSubmit} noValidate>
      <h1 style={styles.title}>Enter your code</h1>
      <p style={styles.text}>Open the authenticator app on your phone and enter the 6-digit code it shows for NYC Suite.</p>
      <label style={styles.label} htmlFor="signin-code">6-digit code</label>
      <input
        id="signin-code" ref={ref} name="code" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
        maxLength={7} value={code} onChange={(e) => onCode(e.target.value)} style={{ ...styles.input, ...styles.codeInput }} disabled={busy}
      />
      <button type="submit" style={styles.primary} disabled={busy}>{busy ? "Checking…" : "Sign in"}</button>
      {triesLeft < 3 && triesLeft > 0 && (
        <p style={styles.hint}>{triesLeft === 1 ? "One more try, then you sign in with your password again." : `${triesLeft} tries left.`}</p>
      )}
      <p style={styles.hint}>Lost your phone? Ivin can reset your authenticator.</p>
      <div style={styles.linkRow}>
        <button type="button" style={styles.link} onClick={onBack} disabled={busy}>Use a different account</button>
      </div>
    </form>
  );
}

export function ResetView({ email, busy, onEmail, onSubmit, onBack }) {
  return (
    <form style={styles.stack} onSubmit={onSubmit} noValidate>
      <h1 style={styles.title}>Reset your password</h1>
      <p style={styles.text}>Enter your email. If it has a Suite account, you will get a link to set a new password.</p>
      <label style={styles.label} htmlFor="reset-email">Email</label>
      <input
        id="reset-email" name="email" type="email" inputMode="email" autoComplete="username"
        autoCapitalize="none" autoCorrect="off" spellCheck={false}
        value={email} onChange={(e) => onEmail(e.target.value)} style={styles.input} disabled={busy}
      />
      <button type="submit" style={styles.primary} disabled={busy}>{busy ? "Sending…" : "Send reset link"}</button>
      <div style={styles.linkRow}>
        <button type="button" style={styles.link} onClick={onBack} disabled={busy}>Back</button>
      </div>
    </form>
  );
}

export function ResetSentView({ email, onBack }) {
  return (
    <div style={styles.stack}>
      <h1 style={styles.title}>Check your email</h1>
      <p style={styles.text}>
        If {email} has a Suite account, a link to set a new password is on its way. Check the spam folder too.
        Set the new password from the link, then come back here and sign in.
      </p>
      <button type="button" style={styles.primary} onClick={onBack}>Back to sign in</button>
    </div>
  );
}

// The couldn't-check screen (App.jsx): the session exists but its details,
// or the Suite profile, could not be read, so nothing opens until a retry
// succeeds or the person signs out.
export function SignInCheckFailed({ text, onRetry, onSignOut }) {
  return (
    <div className="signin-screen" style={styles.screen}>
      <div style={styles.panel}>
        <div style={styles.brand}>
          <LogoMark size={36} />
          <span style={styles.brandName}>NYC Suite</span>
        </div>
        <div style={{ ...styles.stack, marginTop: 24 }}>
          <p style={styles.text}>{text || "The Suite couldn't check your sign-in. Check the internet connection, then try again."}</p>
          <button type="button" style={styles.primary} onClick={onRetry}>Try again</button>
          <button type="button" style={styles.secondary} onClick={onSignOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

function LocalReview() {
  const isLocal = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if (!isLocal) return null;
  const localOrigin = window.location.origin;
  const authOrigin = `${window.location.protocol}//localhost${window.location.port ? `:${window.location.port}` : ""}`;
  return (
    <div className="signin-local" style={styles.localBox}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0, marginBottom: 10 }}>
        Local review
      </div>
      {/* Emulator builds only (compiled out of prod): one-click sign-in as the
          seeded dev operator. */}
      {import.meta.env.VITE_USE_EMULATORS === "true" && (
        <button
          type="button"
          onClick={() => window.__fb?.signIn("op@test.local", "test1234").catch((e) => window.alert(e.message))}
          style={{ ...styles.localLink, border: "none", cursor: "pointer", marginBottom: 10, width: "100%" }}
        >
          Sign in as Dev Operator (emulator)
        </button>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
        <a href={`${localOrigin}/?e2e=quotes`} style={styles.localLink}>Quotes Fixture</a>
        <a href={`${localOrigin}/?e2e=jobs`} style={styles.localLink}>Jobs Fixture</a>
      </div>
      <div style={{ marginTop: 10, fontSize: 11.5, lineHeight: 1.4, color: C.muted }}>
        Sign-in works on localhost here, not on other local addresses. Use{" "}
        <a href={authOrigin} style={{ color: C.text }}>localhost</a>.
      </div>
    </div>
  );
}

export const styles = {
  screen: {
    minHeight: "100vh",
    background: C.bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: font,
    padding: "32px 16px",
    boxSizing: "border-box",
  },
  panel: { width: "100%", maxWidth: 360, textAlign: "center" },
  brand: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 16 },
  brandName: { fontSize: 20, fontWeight: 400, color: C.text },
  sub: { fontSize: 14, fontWeight: 300, color: C.muted, marginBottom: 32 },
  stack: { display: "flex", flexDirection: "column", gap: 10, textAlign: "left" },
  title: { fontSize: 18, fontWeight: 500, color: C.text, margin: 0 },
  text: { fontSize: 15, lineHeight: 1.45, color: C.text, margin: 0 },
  hint: { fontSize: 13, lineHeight: 1.4, color: C.muted, margin: 0 },
  label: { fontSize: 13, color: C.muted, marginBottom: -4 },
  input: {
    fontSize: 16,
    minHeight: 44,
    padding: "10px 12px",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    background: C.surface,
    color: C.text,
    fontFamily: font,
    width: "100%",
    boxSizing: "border-box",
  },
  codeInput: { fontSize: 24, letterSpacing: "0.3em", textAlign: "center", fontVariantNumeric: "tabular-nums" },
  passwordRow: { display: "flex", gap: 8, alignItems: "stretch" },
  showButton: {
    minWidth: 64,
    minHeight: 44,
    padding: "0 12px",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    background: C.surface,
    color: C.text,
    fontSize: 14,
    fontFamily: font,
    cursor: "pointer",
  },
  primary: {
    minHeight: 44,
    padding: "12px 20px",
    background: C.text,
    color: "#FFF",
    border: "none",
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: font,
    width: "100%",
  },
  secondary: {
    minHeight: 44,
    padding: "12px 20px",
    background: C.surface,
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: font,
    width: "100%",
  },
  linkRow: { display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
  link: {
    minHeight: 44,
    padding: "0 4px",
    background: "transparent",
    border: "none",
    color: C.text,
    textDecoration: "underline",
    fontSize: 14,
    fontFamily: font,
    cursor: "pointer",
  },
  error: { marginTop: 16, fontSize: 14, lineHeight: 1.4, color: C.danger, textAlign: "left" },
  info: { marginTop: 16, fontSize: 14, lineHeight: 1.4, color: C.text, textAlign: "left" },
  localBox: {
    margin: "24px auto 0",
    maxWidth: 360,
    padding: 14,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
  },
  localLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "7px 10px",
    background: C.text,
    color: "#FFF",
    borderRadius: 6,
    fontSize: 11.5,
    fontWeight: 400,
    textDecoration: "none",
    fontFamily: font,
  },
};
